import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { FormikHelpers } from "formik";
import _ from "lodash";
import { ReactElement, useState } from "react";
import * as yup from "yup";

import ClickToCopy from "~frontend/components/ClickToCopy";
import { plainScriptFieldValidator } from "~frontend/components/forms/FormPlainScriptField";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import { FormikDialog } from "~frontend/components/forms/FormikDialog";
import FormikErrorBanner from "~frontend/components/forms/FormikErrorBanner";
import {
  MediaFieldValue,
  mediaValidationSchema,
} from "~frontend/components/forms/FormikMediaField";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";
import { palette } from "~frontend/theme";
import { MEDIA_FILE_MAX_SIZE_BYTES } from "~frontend/util/mediaFiles";

import { ScriptEditor } from "../../campaignEditor/Script/ScriptEditor";
import ShaftWarnings from "../../campaignEditor/Script/ShaftWarnings";

export interface KeywordValues {
  keyword: string;
  script: string;
  _submitError: any;
  media?: MediaFieldValue | null;
}

const KEYWORD_LINK_COPY =
  "Use this link to easily share your opt in keyword with contacts. When clicked on a mobile device, the link will open a new text prefilled with your short code number and this keyword.";

export function buildKeywordLink(
  shortcodePhoneNumber: string,
  keyword?: string
): string {
  return encodeURI(
    `sms:${shortcodePhoneNumber}&body=${keyword?.toUpperCase()}`
  );
}

function CopyKeywordLinkSection({ link }: { link: string }): ReactElement {
  return (
    <>
      {KEYWORD_LINK_COPY}
      <Box sx={{ border: `1px solid ${palette.blue}`, borderRadius: 1, my: 1 }}>
        <Typography sx={{ color: palette.blue }}>
          <ClickToCopy text={link} iconSx={{ color: palette.blue }} />
        </Typography>
      </Box>
    </>
  );
}

export function CopyKeywordLinkDialog({
  onClose,
  keyword,
  shortcodePhoneNumber,
}: {
  onClose: () => void;
  keyword?: string;
  shortcodePhoneNumber: string;
}): ReactElement {
  const link = buildKeywordLink(shortcodePhoneNumber, keyword);

  return (
    <Dialog open={!!keyword} sx={{ maxWidth: "540px", margin: "auto" }}>
      <DialogTitle>Copy Keyword Link</DialogTitle>
      <DialogContent>
        <CopyKeywordLinkSection link={link} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="tertiary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function KeywordDialogForm({
  open,
  onClose,
  onSubmit,
  initialValues,
  dialogTitle,
  dialogCopy,
  shortcodePhoneNumber,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    values: KeywordValues,
    formik: FormikHelpers<KeywordValues>
  ) => void;
  initialValues: KeywordValues;
  dialogTitle: string;
  dialogCopy: string;
  shortcodePhoneNumber: string;
}): ReactElement {
  const tenant = useTenant()!;
  const [keywordLinkSectionOpen, setKeywordLinkSectionOpen] = useState(false);

  return (
    <FormikDialog
      open={open}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={yup.object().shape({
        keyword: yup.string().required("Required").min(1).max(100),
        script: plainScriptFieldValidator({ strictLengthLimit: true }, tenant),
        media: mediaValidationSchema(),
      })}
    >
      {({
        isSubmitting,
        values,
        errors,
        touched,
        setFieldTouched,
        handleChange,
      }) => {
        const link = buildKeywordLink(shortcodePhoneNumber, values.keyword);

        return (
          <>
            <DialogTitle sx={{ pb: 0 }}>{dialogTitle}</DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
              <Typography sx={{ mb: 2 }}>{dialogCopy}</Typography>
              <FormikErrorBanner />
              <FormikTextField
                fullWidth
                label="Keyword"
                name="keyword"
                inputProps={{ style: { textTransform: "uppercase" } }}
                onChange={async e => {
                  handleChange(e);
                  await setFieldTouched("keyword", true);
                }}
              />
              {initialValues.keyword &&
                touched.keyword &&
                values.keyword != initialValues.keyword && (
                  <Alert severity="warning" sx={{ mt: -0.5, mb: 2 }}>
                    {
                      "Editing this keyword will create a new keyword link and make the previous one unusable. Copy the new link below to replace it in any existing scripts."
                    }
                  </Alert>
                )}
              <ScriptEditor
                type={"keyword"}
                script={values.script}
                media={values.media || null}
                hideContactFields={true}
                hidePreview={true}
                defaultAlert={
                  <Alert severity="warning">
                    {`Media files can be a picture, video, GIF, or contact card.
                    Files must be jpg, png, gif, mp4, or vcard format and under
                    ${MEDIA_FILE_MAX_SIZE_BYTES / 1000} KB.`}
                  </Alert>
                }
              />
              <ShaftWarnings renderedMessage={values.script} />
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={keywordLinkSectionOpen}
                      onChange={e => {
                        setKeywordLinkSectionOpen(e.target.checked);
                      }}
                    />
                  }
                  label="Generate keyword link"
                />
              </Box>
              <Collapse in={keywordLinkSectionOpen}>
                <CopyKeywordLinkSection link={link} />
              </Collapse>
            </DialogContent>
            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={onClose}
                color="tertiary"
                variant="contained"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || Object.keys(errors).length > 0}
                color={"primary"}
                variant="contained"
              >
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </>
        );
      }}
    </FormikDialog>
  );
}
